import { Injectable } from '@angular/core';
import { CrudAPI, CrudService } from '@core/api';
import { ClientCompany, ClientContact, ClientLocation } from '../models';
import {
  BehaviorSubject,
  catchError,
  distinctUntilChanged,
  filter,
  map,
  of,
  share,
  Subject,
  switchMap,
} from 'rxjs';
// Known leak, ratcheted rather than excused: this value import puts
// DevExtreme's runtime in the NativeScript bundle, measured at 1.53 MiB (9.3%
// of vendor.js) in docs/analysis/mobile-bundle-devextreme-leak.md. The rule errors on
// any NEW devextreme import here; removing these eight disables is what fixing
// that leak means.
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import notify from 'devextreme/ui/notify';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class ClientService {
  private clientContactApi!: CrudAPI<ClientContact>;
  private clientCompanyApi!: CrudAPI<ClientCompany>;
  private clientLocationApi!: CrudAPI<ClientLocation>;
  private clientContacts = new BehaviorSubject<ClientContact[]>([]);
  private clientSearch = new Subject<string>();
  private clientCompanies = new BehaviorSubject<ClientCompany[]>([]);
  private clientLocations = new BehaviorSubject<ClientLocation[]>([]);
  public clientCompany = new BehaviorSubject<ClientCompany>(
    {} as ClientCompany
  );

  public readonly clientCompanies$ = this.clientCompanies.asObservable();
  public readonly clientCompany$ = this.clientCompany.asObservable();
  public readonly clientLocations$ = this.clientLocations.asObservable();
  public readonly clientContact$ = this.clientContacts.asObservable();
  public readonly searchResult$ = this.clientSearch.pipe(
    distinctUntilChanged(),
    filter((x) => x.length >= 3),
    switchMap((query) =>
      this.clientCompanyApi.readMany(
        `?search=company_name:${query}&searchFields=company_name:like`
      )
    ),
    map(({ data }: any) => data), // transform data if necessary
    share()
  );

  constructor(crud: CrudService, private router: Router) {
    this.clientContactApi = crud.forEndpoint('api/crud/clientContactPerson');
    this.clientCompanyApi = crud.forEndpoint('api/crud/clientCompany');
    this.clientLocationApi = crud.forEndpoint('api/crud/clientLocation');
  }

  public search(input: string) {
    this.clientSearch.next(input);
  }

  public loadCompanies() {
    this.clientCompanyApi
      .readMany()
      .pipe(
        map(({ data }: any) => data),
        catchError((e) => {
          notify(e.message, 'error');
          return of([] as ClientCompany[]);
        })
      )
      .subscribe((x) => this.clientCompanies.next(x));
  }

  public getCompany(id: number) {
    this.clientCompanyApi
      .readOne(id)
      .pipe(
        map(({ data }: any) => data),
        catchError((e) => {
          notify(e.message, 'error');
          return of({} as ClientCompany);
        })
      )
      .subscribe((x) => {
        if (x) {
          this.clientCompany.next(x);
        }
      });
  }

  public createClientCompany(clientCompany: ClientCompany) {
    return this.clientCompanyApi.createOne(clientCompany).pipe(
      catchError((e) => {
        notify(e.message, 'error');
        return of({} as ClientCompany);
      })
    );
  }

  public updateClientCompany(clientCompany: Partial<ClientCompany>) {
    return clientCompany.id
      ? this.clientCompanyApi.updateOne(clientCompany.id, clientCompany).pipe(
          catchError((e) => {
            notify(e.message, 'error');
            return of({} as ClientCompany);
          })
        )
      : null;
  }

  public loadLocation(companyId: number) {
    this.clientLocationApi
      .readMany(
        `search=client_company_id:${companyId}&searchFields=client_company_id:=`
      )
      .pipe(
        map(({ data }: any) => data),
        catchError((e) => {
          notify(e.message, 'error');
          return of([] as ClientLocation[]);
        })
      )
      .subscribe((x) => this.clientLocations.next(x));
  }

  public createLocation(location: ClientLocation) {
    this.clientLocationApi
      .createOne(location)
      .pipe(
        catchError((e) => {
          notify(e.message, 'error');
          return e;
        })
      )
      .subscribe((res) => notify(res, 'success'));
  }

  public updateLocation(location: Partial<ClientLocation>) {
    if (location.id) {
      this.clientLocationApi
        .updateOne(location.id, location)
        .pipe(
          catchError((e) => {
            notify(e.message, 'error');
            return of({} as ClientLocation);
          })
        )
        .subscribe((res) => notify(res, 'success'));
    }
  }

  public removeLocation(location: ClientLocation) {
    if (location.id) {
      this.clientLocationApi
        .deleteOne(location.id)
        .pipe(
          catchError((e) => {
            notify(e.message, 'error');
            return e;
          })
        )
        .subscribe((res) => notify(res, 'success'));
    }
  }

  public loadClientContacts(companyId: number) {
    this.clientContactApi
      .readMany(
        `search=client_company_id:${companyId}&searchFields=client_company_id:=`
      )
      .pipe(
        map(({ data }: any) => data),
        catchError((e) => {
          notify(e.message, 'error');
          return of([] as ClientContact[]);
        })
      )
      .subscribe((x) => this.clientContacts.next(x));
  }

  public createClientContact(clientContact: ClientContact) {
    this.clientContactApi
      .createOne(clientContact)
      .pipe(
        catchError((e) => {
          notify(e.message, 'error');
          return e;
        })
      )
      .subscribe((res) => notify(res, 'success'));
  }

  public updateClientContact(clientContact: Partial<ClientLocation>) {
    if (clientContact.id) {
      this.clientContactApi
        .updateOne(clientContact.id, clientContact)
        .pipe(
          catchError((e) => {
            notify(e.message, 'error');
            return e;
          })
        )
        .subscribe((res) => notify(res, 'success'));
    }
  }

  public removeClientContact(clientContact: ClientContact) {
    if (clientContact.id) {
      this.clientContactApi
        .deleteOne(clientContact.id)
        .pipe(
          catchError((e) => {
            notify(e.message, 'error');
            return e;
          })
        )
        .subscribe((res) => notify(res, 'success'));
    }
  }
}
